import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiCall } from "services/api";
import { formatDate } from "utils/formatDate";
import { paramParsify } from "utils/queryParams";

export const useGetStockPerformance = ({ id, type } = {}) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/stock-performance`,
          queryParams: {
            performance_type: type,
            start_date: queryParams?.start_date,
            end_date: queryParams?.end_date,
          },
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id, queryParams, type],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.start_date),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.end_date),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetPriceChartProperty = ({ id }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/chart`,
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetTransactionList = ({
  id,
  affiliated = true,
  limit = 6,
  defaultPeriodStart,
  defaultPeriodEnd,
  enableAutoFetchParams,
}) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/shareholders/transactions`,
          method: "GET",
          queryParams: {
            affiliated,
            limit,
            period_start: queryParams?.period_start || defaultPeriodStart,
            period_end: queryParams?.period_end || defaultPeriodEnd,
            sort_dir: queryParams?.sort_order,
            sort_by: queryParams?.sort_by,
            page: queryParams?.page,
            search: queryParams?.search,
          },
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [affiliated, defaultPeriodEnd, defaultPeriodStart, id, limit, queryParams],
  );

  useEffect(() => {
    if (id && defaultPeriodStart && defaultPeriodEnd) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    defaultPeriodStart,
    defaultPeriodEnd,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchParams
      ? [
          String(queryParams?.page),
          String(queryParams?.search),
          String(queryParams?.sort_by),
          String(queryParams?.sort_order),
        ]
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_start),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_end),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetTransactionDetail = ({ id }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/shareholder/transaction-history/${passedId || id}`,
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetFreeFloatSharesChart = ({ id, defaultDate }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = paramParsify(location?.search);
  const date = queryParams?.date
    ? formatDate.toAPIDate(queryParams?.date)
    : defaultDate;

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/float-shares`,
          method: "GET",
          queryParams: {
            date,
          },
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id, date],
  );

  useEffect(() => {
    if (id && defaultDate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    defaultDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.date),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetBrokerSummaryList = ({
  id,
  enableAutoFetchByParams,
  defaultStartDate,
  defaultEndDate,
}) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = paramParsify(location?.search);
  const today = dayjs();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/broker-summary`,
        queryParams: {
          start_date:
            queryParams?.start_date ??
            defaultStartDate ??
            formatDate.toAPIDate(today),
          end_date:
            queryParams?.end_date ??
            defaultEndDate ??
            formatDate.toAPIDate(today),
        },
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [defaultEndDate, defaultStartDate, id, queryParams, today]);

  useEffect(() => {
    if (id && defaultStartDate && defaultEndDate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    defaultStartDate,
    defaultEndDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchByParams
      ? [String(queryParams?.start_date), String(queryParams?.end_date)]
      : []),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetTransactionVolumeChart = ({
  id,
  affiliated = false,
  transaction_type,
}) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/shareholders/transactions/shares-vol/${transaction_type}`,
        method: "GET",
        queryParams: {
          affiliated,
          period_start: queryParams?.period_start || queryParams?.start_date,
          period_end: queryParams?.period_end || queryParams?.end_date,
        },
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [affiliated, id, queryParams, transaction_type]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_start),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_end),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.start_date),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.end_date),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetTransactionNumberChart = ({
  id,
  affiliated = false,
  transaction_type,
}) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/shareholders/transactions/counter/${transaction_type}`,
        method: "GET",
        queryParams: {
          affiliated,
          period_start: queryParams?.period_start || queryParams?.start_date,
          period_end: queryParams?.period_end || queryParams?.end_date,
        },
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [affiliated, id, queryParams, transaction_type]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_start),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.period_end),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.start_date),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.end_date),
  ]);

  return { response, loading, error, refetch: fetchData };
};
